import {useInitialDateNow, useIsEndTimeReached} from '@aboutyou/i18n';
import {Log} from '@aboutyou/mlm';
import {useMergedFeatureFlagConfig} from './Provider';
import {useConfigWithExperimentData} from './abtests/useConfigWithExperimentData';
import {getExperimentValue} from './helpers';
import {AvailableFeatureNames, FeatureToggleConfig} from './types';

type TimeFrame = NonNullable<FeatureToggleConfig['timeFrame']>;

/**
 * @deprecated use useIsFeatureActive instead
 *
 * Can be used to change a code path based on a global configuration or active ab test cookies.
 * The soft feature toggle can change at runtime, as it might be relative to a time specific locale.
 *
 * Compared to a hard feature flag, this will not allow to code split and optimize the code.
 *
 * @param featureName The name of the feature flag. This name needs to be present in the config,
 *                    which by default is defined in `packages/libraries/feature-flags/defaults.ts`.
 * @param config      Allows to pass defaults for feature flag. This defaults to the default config maintained in the feature flag repository.
 *                    This property is not intended to be used in any production code,
 *                    it was mainly implemented for easier testing.
 *
 * @returns returns   a boolean indicating either the feature is active or not
 *                    or a string that matches possible feature state values of experiment running.
 *                    Return value is based on Time frame, Locale, Experiment State.
 */
export const useIsSoftFeatureToggleActive = (
  featureName: AvailableFeatureNames,
  expensiveDateNow?: number,
): boolean | string => {
  const mergedFeatureFlagConfig = useMergedFeatureFlagConfig();
  const currentConfig = mergedFeatureFlagConfig[featureName];
  const combinedConfig = useConfigWithExperimentData(featureName);

  // A flag's timeFrame is fixed for the lifetime of the page, so this
  // condition never changes between renders and the hook order stays stable.
  const timeFrameState = combinedConfig.timeFrame
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useTimeFrameState(combinedConfig.timeFrame, expensiveDateNow)
    : undefined;

  if (currentConfig === undefined) {
    Log.warn(
      '@aboutyou/feature-flags',
      `Tried to access an unknown feature flag. To remove this warning either go to \`packages/libraries/feature-flags/defaults.ts\` and add it or remove this check from the code.`,
      {
        featureName,
      },
    );
    return false;
  }

  if (combinedConfig.timeFrame?.end && timeFrameState?.isEndTimeReached) {
    return combinedConfig.timeFrame.end.value;
  }

  if ('experiment' in combinedConfig && !!combinedConfig.experiment) {
    return getExperimentValue(combinedConfig.experiment);
  }

  if (combinedConfig.timeFrame && timeFrameState) {
    const isStartTimeHasBeenReached = startTimeHasBeenReached({
      timeFrame: combinedConfig.timeFrame,
      featureName,
      ...timeFrameState,
    });

    if (isStartTimeHasBeenReached !== undefined) {
      return isStartTimeHasBeenReached;
    }
  }

  return combinedConfig.defaultValue ?? false;
};

const useTimeFrameState = (timeFrame: TimeFrame, expensiveDateNow?: number) => {
  const initialDateNow = useInitialDateNow();
  const anyDateBeforeToday = new Date(0);
  const isEndTimeReached = useIsEndTimeReached(
    new Date(timeFrame.end?.time || anyDateBeforeToday),
    60000,
  );

  return {
    isEndTimeReached,
    dateNow: expensiveDateNow || initialDateNow,
  };
};

const startTimeHasBeenReached = ({
  timeFrame,
  isEndTimeReached,
  featureName,
  dateNow,
}: {
  timeFrame: TimeFrame;
  isEndTimeReached: boolean;
  featureName: AvailableFeatureNames;
  dateNow: number;
}) => {
  const startTime = new Date(timeFrame.start.time).getTime();

  if (isNaN(startTime)) {
    Log.warn(
      '@aboutyou/feature-flags',
      `Tried to parse start time. Failed because of invalid date format.`,
      {
        featureName,
      },
    );

    return undefined;
  }

  if (timeFrame.end?.time && isEndTimeReached) {
    return undefined;
  }

  if (dateNow - startTime < 0) {
    return undefined;
  }

  return timeFrame.start.value;
};
